import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { callApi } from "@/api/callApi";
import { API_ENDPOINT } from "@/api/constant";
import type { IUser } from "@/interface/auth.model";

interface UserState {
  isHolidayLoading: boolean;
  isAuthenticated: boolean;
  user: IUser | null;
  role: string | null;
  isSideBarOpen: number;
  isActiveTab: string;
  userWebsiteId: string;
  loading: "idle" | "pending" | "succeeded" | "failed";
  userDetailsLoading: "idle" | "pending" | "succeeded" | "failed";
  isLoadingOrder: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
  notificationCount: number;
  scrollPosition: number;
  isTermsModalOpen: boolean;
}

const initialState: UserState = {
  isAuthenticated: false,
  isHolidayLoading: false,
  user: null,
  isSideBarOpen: 0,
  isActiveTab: "",
  userWebsiteId: "",
  loading: "idle",
  userDetailsLoading: "idle",
  isLoadingOrder: "idle",
  notificationCount: 0,
  error: null,
  role: null,
  scrollPosition: 0,
  isTermsModalOpen: false,
};

export const fetchNotificationCount = createAsyncThunk(
  "user/fetchNotificationCount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(`${API_ENDPOINT.notification}/count/unseen`, {
        method: "GET",
      });
      return response?.data?.count;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch notifications");
    }
  }
);

// Called after login (or on app refresh) to hydrate the user profile.
// This is NOT required for the redirect to succeed — login success alone
// is enough to move to the dashboard; this just fills in the user's name etc.
export const fetchUserDetails = createAsyncThunk<IUser>(
  "user/fetchUserDetails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(API_ENDPOINT.profile, {
        method: "GET",
      });
      return (response?.data ?? response) as IUser;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch user details");
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setUserWebsiteId: (state, action: PayloadAction<string>) => {
      state.userWebsiteId = action.payload;
    },
    setRole: (state, action: PayloadAction<string>) => {
      state.role = action.payload;
    },
    setNotificationCount: (state, action: PayloadAction<number>) => {
      state.notificationCount = action.payload;
    },
    setSideBarOpen: (state, action: PayloadAction<number>) => {
      state.isSideBarOpen = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.isActiveTab = action.payload;
    },
    logout: (state) => {
      Object.assign(state, initialState);
      state.isAuthenticated = false;
    },
    saveSideBarScrollPosition: (state, action: PayloadAction<number>) => {
      state.scrollPosition = action.payload;
    },
    openTermsModal: (state) => {
      state.isTermsModalOpen = true;
    },
    closeTermsModal: (state) => {
      state.isTermsModalOpen = false;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationCount.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchNotificationCount.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = "succeeded";
        state.notificationCount = action.payload;
      })
      .addCase(fetchNotificationCount.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload as string;
      })
      .addCase(fetchUserDetails.pending, (state) => {
        state.userDetailsLoading = "pending";
      })
      .addCase(fetchUserDetails.fulfilled, (state, action: PayloadAction<IUser>) => {
        state.userDetailsLoading = "succeeded";
        state.user = { ...state.user, ...action.payload };
        state.isAuthenticated = true;
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.userDetailsLoading = "failed";
        state.error = action.payload as string;
        // NOTE: do not flip isAuthenticated to false here — login already
        // set the tokens; a failed profile fetch shouldn't log the user out.
      });
  },
});

export const {
  setAuthenticated,
  setSideBarOpen,
  setActiveTab,
  logout,
  setUserWebsiteId,
  setRole,
  setNotificationCount,
  saveSideBarScrollPosition,
  openTermsModal,
  closeTermsModal,
} = userSlice.actions;

export default userSlice.reducer;